/**
 * Wallet Card Component
 * Display individual wallet information in a modern card format
 * with real-time balance data and glassmorphism effects
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useWalletBalance } from '../../hooks/wallet-tracker/useWalletBalance';
import { TRACKER_COLORS } from '../../config/monitorColors';
import type { TrackedWallet } from '../../types/wallet.types';

// ============================================
// CONSTANTS & STYLES
// ============================================

// Tag configurations with colors
const TAG_CONFIG: Record<string, { emoji: string; color: string }> = {
  'smart-money': { emoji: '🧠', color: '#8b5cf6' },
  'whale': { emoji: '🐋', color: '#3b82f6' },
  'defi-pro': { emoji: '🏦', color: '#10b981' },
  'nft-trader': { emoji: '🎨', color: '#ec4899' },
  'influencer': { emoji: '⭐', color: '#f59e0b' },
  'fund': { emoji: '💼', color: '#6366f1' },
  'personal': { emoji: '👤', color: '#64748b' },
};

interface WalletCardProps {
  wallet: TrackedWallet;
  onSelect?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function WalletCard({ wallet, onSelect, onEdit, onDelete }: WalletCardProps) {
  const { t } = useTranslation();
  const [showActions, setShowActions] = useState(false);

  // Fetch wallet balance using React Query
  const { data: balanceData, isLoading, error } = useWalletBalance(wallet.address);

  // Format address for display
  const displayAddress = wallet.ensName || `${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`;

  // Calculate time since added
  const timeAgo = () => {
    const now = new Date();
    const added = new Date(wallet.addedAt);
    const diffMs = now.getTime() - added.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('walletTracker.time.justNow', 'Just now');
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 30) return `${diffDays}d`;
    return added.toLocaleDateString();
  };

  // Get status color
  const getStatusColor = () => {
    if (isLoading) return TRACKER_COLORS.warning;
    if (error) return TRACKER_COLORS.error;
    return TRACKER_COLORS.success;
  };

  return (
    <div
      style={{
        background: 'rgba(6, 182, 212, 0.05)',
        borderRadius: '14px',
        border: '1px solid rgba(6, 182, 212, 0.15)',
        padding: '18px',
        cursor: onSelect ? 'pointer' : 'default',
        position: 'relative',
        transition: 'all 0.2s ease',
      }}
      onClick={onSelect}
      onMouseEnter={(e) => {
        setShowActions(true);
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(6, 182, 212, 0.15)';
        e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.3)';
      }}
      onMouseLeave={(e) => {
        setShowActions(false);
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.15)';
      }}
    >
      {/* Header - Label & Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            {/* Wallet Icon */}
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: `linear-gradient(135deg, ${TRACKER_COLORS.primary}, ${TRACKER_COLORS.secondary})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icons.Wallet size={16} color="white" />
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: 600,
                margin: 0,
                color: 'var(--text)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {wallet.label}
                {isLoading && (
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: TRACKER_COLORS.warning,
                    animation: 'pulse 1s infinite',
                  }} />
                )}
              </h3>
            </div>
          </div>

          {/* Address */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            fontFamily: 'monospace',
            color: 'var(--muted)',
          }}>
            <span>{displayAddress}</span>
            <a
              href={`https://etherscan.io/address/${wallet.address}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                color: TRACKER_COLORS.primary,
                display: 'flex',
                alignItems: 'center',
                opacity: 0.7,
              }}
            >
              <Icons.ExternalLink size={12} />
            </a>
          </div>
        </div>

        {/* Actions Menu */}
        <div style={{
          display: 'flex',
          gap: '4px',
          opacity: showActions ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}>
          {onEdit && (
            <button
              style={{
                padding: '6px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '6px',
                color: 'var(--text)',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title={t('common.edit', 'Edit')}
            >
              <Icons.Edit size={14} />
            </button>
          )}
          {onDelete && (
            <button
              style={{
                padding: '6px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: 'none',
                borderRadius: '6px',
                color: TRACKER_COLORS.error,
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`${t('walletTracker.confirm.delete', 'Delete')} ${wallet.label}?`)) {
                  onDelete();
                }
              }}
              title={t('common.delete', 'Delete')}
            >
              <Icons.Trash size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Tags */}
      {wallet.tags && wallet.tags.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          marginBottom: '14px',
        }}>
          {wallet.tags.map(tag => {
            const config = TAG_CONFIG[tag] || { emoji: '🏷️', color: '#64748b' };
            return (
              <span
                key={tag}
                style={{
                  fontSize: '10px',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  background: `${config.color}15`,
                  color: config.color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: 600,
                }}
              >
                <span>{config.emoji}</span>
                <span style={{ textTransform: 'capitalize' }}>{tag.replace('-', ' ')}</span>
              </span>
            );
          })}
        </div>
      )}

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        padding: '14px 0',
        borderTop: '1px solid rgba(255, 255, 255, 0.06)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
      }}>
        {/* Total Value */}
        <div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Total
          </div>
          <div style={{
            fontSize: '15px',
            fontWeight: 600,
            color: isLoading ? 'var(--muted)' : 'var(--text)',
          }}>
            {isLoading ? (
              <span style={{ opacity: 0.5 }}>...</span>
            ) : error ? (
              <span style={{ color: TRACKER_COLORS.error, fontSize: '11px' }}>Error</span>
            ) : balanceData?.totalValueUSD ? (
              `$${balanceData.totalValueUSD >= 1000
                ? (balanceData.totalValueUSD / 1000).toFixed(1) + 'k'
                : balanceData.totalValueUSD.toFixed(0)}`
            ) : (
              '$0'
            )}
          </div>
        </div>

        {/* ETH Balance */}
        <div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            ETH
          </div>
          <div style={{
            fontSize: '15px',
            fontWeight: 600,
            color: isLoading ? 'var(--muted)' : 'var(--text)',
          }}>
            {isLoading ? (
              <span style={{ opacity: 0.5 }}>...</span>
            ) : error ? (
              '-'
            ) : balanceData ? (
              parseFloat(balanceData.ethBalanceFormatted) > 0
                ? parseFloat(balanceData.ethBalanceFormatted).toFixed(4)
                : '0'
            ) : (
              '-'
            )}
          </div>
        </div>

        {/* Token Count */}
        <div>
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Tokens
          </div>
          <div style={{
            fontSize: '15px',
            fontWeight: 600,
            color: isLoading ? 'var(--muted)' : 'var(--text)',
          }}>
            {isLoading ? (
              <span style={{ opacity: 0.5 }}>...</span>
            ) : error ? (
              '-'
            ) : balanceData ? (
              balanceData.tokenCount
            ) : (
              '-'
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && !isLoading && (
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '8px',
          fontSize: '11px',
          color: TRACKER_COLORS.error,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Icons.AlertCircle size={14} />
            <span style={{ fontWeight: 600 }}>{t('walletTracker.error.balanceFetch', 'Unable to fetch balance')}</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '12px',
        fontSize: '11px',
        color: 'var(--muted)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Icons.Clock size={12} />
          <span>{t('walletTracker.card.added', 'Added')} {timeAgo()}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: getStatusColor(),
            boxShadow: `0 0 6px ${getStatusColor()}`,
          }} />
          <span>{balanceData?.provider || 'Multi-chain'}</span>
        </div>
      </div>

      {/* Notes Preview */}
      {wallet.notes && (
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '8px',
          fontSize: '11px',
          color: 'var(--muted)',
          fontStyle: 'italic',
          borderLeft: `2px solid ${TRACKER_COLORS.primary}40`,
        }}>
          "{wallet.notes.length > 80 ? wallet.notes.slice(0, 80) + '...' : wallet.notes}"
        </div>
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
